import type { ElementRef, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';

import { Widgets } from '@common/enums';

import { flexAlignVar, textAlignVar } from '@sites/entity-core';
import type { ColumnSpecificForm } from '@sites/entity-signal-form';

import type {
    DenormalizedSpecificBlockEntity,
    DenormalizedSpecificSectionEntity,
    WebLegacyWidgetEntity,
} from '@web/types';

import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';

import { SpecificWidgetComponent } from '@web/layout/content/page-content/components/widgets/specific-widget/specific-widget.component';

import type { ColumnType } from '../base-column.abstract';
import { BaseColumnComponent } from '../base-column.abstract';

@Component({
    selector: 'web-specific-column',
    templateUrl: './specific-column.component.html',
    styleUrls: ['./specific-column.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkDropList, SpecificWidgetComponent],
})
export class SpecificColumnComponent extends BaseColumnComponent<DenormalizedSpecificSectionEntity> implements OnInit {
    public readonly block = input.required<DenormalizedSpecificBlockEntity>();
    public readonly blockHasLogo = input<boolean>(false);

    public readonly columnType: ColumnType = 'specific';

    private readonly shopCartIconFacade: ShopCartIconFacade = inject(ShopCartIconFacade);

    /**
     * Alignment is read from the signal form rather than the entity input, the
     * way `default-tab` does it, so each variable only rewrites when its own
     * source changes.
     */
    private form!: ColumnSpecificForm;

    private readonly animatedElem = viewChild.required<ElementRef<HTMLDivElement>>('animatedElem');

    private readonly isCartEnabled = toSignal(this.shopCartIconFacade.isCartEnabled$, { initialValue: false });

    /** Drop targets for the widget drag — every header column on the page. */
    public readonly connectedTo = toSignal(this.blocksFacade.specificColumnConnectedToList$, {
        initialValue: [] as string[],
    });

    /**
     * Written once, from the slot the column was created in, and never edited
     * afterwards — the settings panel is unreachable for a header column.
     */
    private readonly horizontalAlign = computed(() => this.form.horizontalAlign().value());
    /**
     * Alignment the row falls back to once its slots stop fitting in a line. The
     * block still stores `L | M | R`, hence its own mapper.
     */
    private readonly narrowAlign = computed(() => this.block().props.horizontalPosition);

    /** Styling of the header column — see `column.base.less`. */
    public readonly justify = computed(() => flexAlignVar(this.horizontalAlign()));
    public readonly align = computed(() => flexAlignVar(this.parentEntity().props.verticalPosition));
    public readonly textAlign = computed(() => textAlignVar(this.horizontalAlign()));
    public readonly narrowJustify = computed(() => flexAlignVar(this.narrowAlign()));
    public readonly narrowTextAlign = computed(() => textAlignVar(this.narrowAlign()));

    constructor() {
        super();
        this.replayAnimationOnChange(this.animatedElem);
    }

    public ngOnInit(): void {
        this.form = this.columnFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as ColumnSpecificForm;
    }

    /**
     * The cart icon only shows once the store has a cart — except on mobile,
     * where the header keeps the slot so the layout does not jump.
     */
    public isWidgetVisible(widget: WebLegacyWidgetEntity): boolean {
        return widget.name !== Widgets.SHOP_CART_ICON || this.isCartEnabled() || this.isMobile();
    }

    public override dropWidget(event: CdkDragDrop<WebLegacyWidgetEntity[]>): void {
        const { item, container, previousContainer, currentIndex, previousIndex } = event;

        if (container === previousContainer && previousIndex === currentIndex) {
            return;
        }

        const widget = item.data as WebLegacyWidgetEntity;
        this.widgetsFacade.moveWidgetToColumn(
            widget.id,
            previousContainer.id,
            container.id,
            previousIndex,
            currentIndex
        );
    }
}
